import {
  FETCH_EXPERIENCE,
  FETCH_EXPERIENCE_LIST,
  FETCH_EXPERIENCE_DATES,
  FETCH_EXPERIENCE_TIMESLOTS,
  FETCH_EXPERIENCES,
  FETCH_EXPERIENCE_CATEGORIES,
  FETCH_EXPERIENCE_LIST_FACETS,
  FETCH_EXPERIENCE_VOUCHER,
  FETCH_BOOKING_DETAILS,
  MARK_EXPERIENCE_BOOKING_DETAILS_AS_REDEEMED,
  FETCH_TRANSFER_OPTIONS,
  USER_LOGOUT,
  USER_LOGIN,
  FETCH_REFUND_DETAILS,
  VALIDATE_EXPERIENCE_ITEMS,
  FETCH_TOP_ATTRACTIONS,
  FETCH_ATTRACTION_OFFERS,
} from 'actions/apiActionConstants'
import { createAppReducer, apiReducerSwitch } from 'lib/redux/reducerUtils'
import {
  API_CALL_REQUEST,
  API_CALL_FAILURE,
  API_CALL_SUCCESS,
  EXPERIENCE_VIEWED,
  SET_EXPERIENCE_PLACE,
  SET_CANCELLING_EXPERIENCES,
  CHECKOUT_INITIALISE,
  CHECKOUT_PROCESS_SUCCESS,
  CLEAR_EXPERIENCE_TIMES,
  CLEAR_EXPERIENCE,
  OFFER_SET_EXPERIENCES_SEARCH_RESULT_METADATA,
} from 'actions/actionConstants'
import { arrayToObject, take, unique } from 'lib/array/arrayUtils'
import type { AnyAction } from 'redux'

export const initialExperienceState: App.ExperienceState = {
  experiencePlace: { type: 'currentLocation', userSelected: false },
  experiences: {},
  experienceErrors: {},
  bookingDetails: {},
  bookingDetailsErrors: {},
  fetchingBookingDetails: {},
  fetchingExperiences: {},
  refundDetails: {},
  fetchingRefundDetails: {},
  refundDetailsErrors: {},
  experienceLists: {},
  experienceOffersMetadata: {},
  experienceDates: {},
  experienceTimes: {},
  experienceTransferOptions: {},
  experienceCategories: [],
  experienceCategoriesLocale: undefined,
  experienceListFacets: {},
  experienceVoucher: {},
  recentlyViewedExperienceIds: [],
  fetchingCategories: false,
  experienceCategoriesError: undefined,
  markingBookingDetailsAsRedeemed: {},
  cancellingExperiences: undefined,
  experienceItemsValidation: {},
  topAttractions: {},
  attractionOffers: {},
}

const apiCalls = apiReducerSwitch<App.ExperienceState>({
  [FETCH_EXPERIENCE_LIST]: {
    request: (state, action) => ({
      experienceLists: {
        ...state.experienceLists,
        [action.key]: {
          ids: [],
          fetching: true,
          error: undefined,
          total: 0,
        },
      },
    }),
    success: (state, action) => ({
      experienceLists: {
        ...state.experienceLists,
        [action.key]: {
          ids: action.data.experienceIds,
          total: action.data.total,
          fetching: false,
        },
      },
    }),
    failure: (state, action) => ({
      experienceLists: {
        ...state.experienceLists,
        [action.key]: {
          ids: [],
          fetching: false,
          error: action.error,
          total: 0,
        },
      },
    }),
  },
  [FETCH_EXPERIENCE_DATES]: {
    request: (state, action) => ({
      experienceDates: {
        ...state.experienceDates,
        [action.experienceId]: {
          ...state.experienceDates[action.experienceId],
          [action.key]: {
            ...state.experienceDates[action.experienceId]?.[action.key],
            fetching: true,
            error: undefined,
          },
        },
      },
    }),
    success: (state, action) => ({
      experienceDates: {
        ...state.experienceDates,
        [action.experienceId]: {
          ...state.experienceDates[action.experienceId],
          [action.key]: {
            ...state.experienceDates[action.experienceId]?.[action.key],
            dates: action.data,
            fetching: false,
          },
        },
      },
    }),
    failure: (state, action) => ({
      experienceDates: {
        ...state.experienceDates,
        [action.experienceId]: {
          ...state.experienceDates[action.experienceId],
          [action.key]: {
            ...state.experienceDates[action.experienceId]?.[action.key],
            fetching: false,
            error: action.error,
          },
        },
      },
    }),
  },
  [FETCH_EXPERIENCE_TIMESLOTS]: {
    request: (state, action) => ({
      experienceTimes: {
        ...state.experienceTimes,
        [action.experienceId]: {
          ...state.experienceTimes[action.experienceId],
          [action.key]: {
            ...state.experienceTimes[action.experienceId]?.[action.key],
            fetching: true,
            error: undefined,
          },
        },
      },
    }),
    success: (state, action) => ({
      experienceTimes: {
        ...state.experienceTimes,
        [action.experienceId]: {
          ...state.experienceTimes[action.experienceId],
          [action.key]: {
            ...state.experienceTimes[action.experienceId]?.[action.key],
            slots: action.data,
            fetching: false,
            error: undefined,
          },
        },
      },
    }),
    failure: (state, action) => ({
      experienceTimes: {
        ...state.experienceTimes,
        [action.experienceId]: {
          ...state.experienceTimes[action.experienceId],
          [action.key]: {
            ...state.experienceTimes[action.experienceId]?.[action.key],
            fetching: false,
            error: action.error,
          },
        },
      },
    }),
  },
  [FETCH_EXPERIENCE]: {
    request: (state, action) => ({
      fetchingExperiences: {
        ...state.fetchingExperiences,
        [action.id]: true,
      },
    }),
    success: (state, action) => ({
      experiences: {
        ...state.experiences,
        [action.id]: action.data,
      },
      fetchingExperiences: {
        ...state.fetchingExperiences,
        [action.id]: false,
      },
    }),
    failure: (state, action) => ({
      fetchingExperiences: {
        ...state.fetchingExperiences,
        [action.id]: false,
      },
      experienceErrors: {
        ...state.experienceErrors,
        [action.id]: action.error,
      },
    }),
  },
  [FETCH_BOOKING_DETAILS]: {
    request: (state, action) => ({
      fetchingBookingDetails: {
        ...state.fetchingBookingDetails,
        ...arrayToObject(action.ids, id => id, () => true),
      },
    }),
    success: (state, action) => ({
      bookingDetails: {
        ...state.bookingDetails,
        ...arrayToObject(action.data, bookingDetail => bookingDetail.experienceItemId),
      },
      fetchingBookingDetails: {
        ...state.fetchingBookingDetails,
        ...arrayToObject(action.ids, id => id, () => false),
      },
    }),
    failure: (state, action) => ({
      fetchingBookingDetails: {
        ...state.fetchingBookingDetails,
        ...arrayToObject(action.ids, id => id, () => false),
      },
      bookingDetailsErrors: {
        ...state.bookingDetailsErrors,
        ...arrayToObject(action.ids, id => id, () => action.error),
      },
    }),
  },
  [FETCH_REFUND_DETAILS]: {
    request: (state, action) => ({
      fetchingRefundDetails: {
        ...state.fetchingRefundDetails,
        ...arrayToObject(action.ids, id => id, () => true),
      },
    }),
    success: (state, action) => ({
      refundDetails: {
        ...state.refundDetails,
        ...arrayToObject(action.data, refundDetail => refundDetail.orderItemId),
      },
      fetchingRefundDetails: {
        ...state.fetchingRefundDetails,
        ...arrayToObject(action.ids, id => id, () => false),
      },
    }),
    failure: (state, action) => ({
      fetchingRefundDetails: {
        ...state.fetchingRefundDetails,
        ...arrayToObject(action.ids, id => id, () => false),
      },
      refundDetailsErrors: {
        ...state.refundDetailsErrors,
        ...arrayToObject(action.ids, id => id, () => action.error),
      },
    }),
  },
  [MARK_EXPERIENCE_BOOKING_DETAILS_AS_REDEEMED]: {
    request: (state, action) => ({
      markingBookingDetailsAsRedeemed: {
        ...state.markingBookingDetailsAsRedeemed,
        [action.experienceItemId]: true,
      },
    }),
    success: (state, action) => ({
      markingBookingDetailsAsRedeemed: {
        ...state.markingBookingDetailsAsRedeemed,
        [action.experienceItemId]: false,
      },
      bookingDetails: {
        ...state.bookingDetails,
        [action.experienceItemId]: { ...state.bookingDetails[action.experienceItemId], markedAsRedeemed: true },
      },
    }),
    failure: (state, action) => ({
      markingBookingDetailsAsRedeemed: {
        ...state.markingBookingDetailsAsRedeemed,
        [action.experienceItemId]: false,
      },
    }),
  },
  [FETCH_EXPERIENCES]: {
    request: (state, action) => ({
      fetchingExperiences: {
        ...state.fetchingExperiences,
        ...arrayToObject(action.ids, id => id, () => true),
      },
    }),
    success: (
      state,
      action: AnyAction & { data: { experiences: Array<App.ExperienceOffer>, errors: Record<string, any> } },
    ) => {
      return {
        experiences: {
          ...state.experiences,
          ...arrayToObject(action.data.experiences, experience => experience.id),
        },
        experienceErrors: {
          ...state.experienceErrors,
          ...action.data.errors,
        },
        fetchingExperiences: {
          ...state.fetchingExperiences,
          ...arrayToObject((action.ids as Array<string>), id => id, () => false),
        },
      }
    },
    failure: (state, action) => ({
      fetchingExperiences: {
        ...state.fetchingExperiences,
        ...arrayToObject(action.ids, id => id, () => false),
      },
      fetchingErrors: {
        ...state.experienceErrors,
        ...arrayToObject(action.ids, id => id, () => action.error),
      },
    }),
  },
  [FETCH_EXPERIENCE_LIST_FACETS]: {
    request: (state, action) => ({
      experienceListFacets: {
        ...state.experienceListFacets,
        [action.key]: {
          fetching: true,
        },
      },
    }),
    success: (state, action) => ({
      experienceListFacets: {
        ...state.experienceListFacets,
        [action.key]: {
          fetching: false,
          facets: action.data,
        },
      },
    }),
    failure: (state, action) => ({
      experienceListFacets: {
        ...state.experienceListFacets,
        [action.key]: {
          fetching: false,
          error: action.error,
        },
      },
    }),
  },
  [FETCH_EXPERIENCE_VOUCHER]: {
    request: (state, action) => ({
      experienceVoucher: {
        ...state.experienceVoucher,
        [action.experienceItemId]: {
          fetching: true,
          error: undefined,
        },
      },
    }),
    success: (state, action) => ({
      experienceVoucher: {
        ...state.experienceVoucher,
        [action.experienceItemId]: {
          fetching: false,
          data: action.data,
        },
      },
    }),
    failure: (state, action) => ({
      experienceVoucher: {
        ...state.experienceVoucher,
        [action.experienceItemId]: {
          fetching: false,
          error: action.error,
        },
      },
    }),
  },
  [FETCH_EXPERIENCE_CATEGORIES]: {
    request: () => ({
      fetchingCategories: true,
      experienceCategoriesError: undefined,
    }),
    success: (state, action) => ({
      experienceCategories: action.data,
      experienceCategoriesLocale: action.locale,
      fetchingCategories: false,
    }),
    failure: (state, action) => ({
      fetchingCategories: false,
      experienceCategoriesError: action.error,
      experienceCategoriesLocale: action.locale,
    }),
  },
  [FETCH_TRANSFER_OPTIONS]: {
    request: (state, action) => ({
      experienceTransferOptions: {
        ...state.experienceTransferOptions,
        [action.experienceId]: {
          fetching: true,
        },
      },
    }),
    success: (state, action) => ({
      experienceTransferOptions: {
        ...state.experienceTransferOptions,
        [action.experienceId]: {
          ...state.experienceTransferOptions[action.experienceId],
          fetching: false,
          transfers: action.data,
        },
      },
    }),
    failure: (state, action) => ({
      experienceTransferOptions: {
        ...state.experienceTransferOptions,
        [action.experienceId]: {
          fetching: false,
          error: action.error,
        },
      },
    }),
  },
  [USER_LOGOUT]: {
    success: () => ({
      experienceDates: {},
      experienceTimes: {},
    }),
  },
  [USER_LOGIN]: {
    success: () => ({
      experienceDates: {},
      experienceTimes: {},
    }),
  },
  [VALIDATE_EXPERIENCE_ITEMS]: {
    request: (state, action) => ({
      experienceItemsValidation: {
        ...state.experienceItemsValidation,
        ...(
          arrayToObject(action.bookingItems,
            (bookingItem) => bookingItem.experienceItemId,
            (bookingItem) => ({
              transactionKey: bookingItem.transactionKey,
              fetching: true,
            }),
          )),
      },
    }),
    success: (state, action) => ({
      experienceItemsValidation: {
        ...state.experienceItemsValidation,
        ...(arrayToObject(action.data.successfulItemsIds, (itemId) => itemId, (itemId) => ({
          ...state.experienceItemsValidation[itemId],
          fetching: false,
          isValid: true,
        }))),
        ...(arrayToObject(action.data.failedItems, (failedItem) => failedItem.itemId, (failedItem) => ({
          ...state.experienceItemsValidation[failedItem.itemId],
          fetching: false,
          isValid: false,
          error: failedItem.message,
        }))),
      },
    }),
    failure: (state, action) => ({
      experienceItemsValidation: {
        ...state.experienceItemsValidation,
        ...(arrayToObject(action.bookingItems, (bookingItem) => bookingItem.experienceItemId, (bookingItem) => ({
          ...state.experienceItemsValidation[bookingItem.experienceItemId],
          // Doesn't block booking if check endpoint fails
          isValid: true,
          fetching: false,
        }))),
      },
    }),
  },
  [FETCH_TOP_ATTRACTIONS]: {
    request: (state, action) => {
      const prev = state.topAttractions[action.key]
      return {
        topAttractions: {
          ...state.topAttractions,
          [action.key]: {
            ...prev,
            fetching: true,
            error: undefined,
          },
        },
      }
    },
    success: (state, action) => ({
      topAttractions: {
        ...state.topAttractions,
        [action.key]: {
          attractions: action.data.attractions,
          total: action.data.total,
          topAttractionsLimit: action.data.topAttractionsLimit,
          isCuratedDestination: action.data.isCuratedDestination,
          fetching: false,
          lastFetchedAt: Date.now(),
        },
      },
    }),
    failure: (state, action) => ({
      topAttractions: {
        ...state.topAttractions,
        [action.key]: {
          attractions: [],
          total: 0,
          fetching: false,
          error: action.error,
        },
      },
    }),
  },
  [FETCH_ATTRACTION_OFFERS]: {
    request: (state, action) => {
      const prev = state.attractionOffers[action.key]
      return {
        attractionOffers: {
          ...state.attractionOffers,
          [action.key]: {
            ...prev,
            fetching: true,
            error: undefined,
          },
        },
      }
    },
    success: (state, action) => ({
      attractionOffers: {
        ...state.attractionOffers,
        [action.key]: {
          offerIds: action.data.offers.map((offer: Experiences.AttractionOffer) => offer.id),
          total: action.data.total,
          fetching: false,
          lastFetchedAt: Date.now(),
        },
      },
    }),
    failure: (state, action) => ({
      attractionOffers: {
        ...state.attractionOffers,
        [action.key]: {
          offerIds: [],
          total: 0,
          fetching: false,
          error: action.error,
        },
      },
    }),
  },
})

export default createAppReducer<App.ExperienceState>(initialExperienceState, {
  [API_CALL_REQUEST]: (state, action) => apiCalls(state, action),
  [API_CALL_FAILURE]: (state, action) => apiCalls(state, action),
  [API_CALL_SUCCESS]: (state, action) => apiCalls(state, action),
  [EXPERIENCE_VIEWED]: (state, action) => ({
    recentlyViewedExperienceIds: take(
      unique([action.id, ...state.recentlyViewedExperienceIds]),
      5,
    ),
  }),
  [SET_EXPERIENCE_PLACE]: (state, action) => ({
    experiencePlace: {
      placeId: action.placeId,
      type: action.placeType,
      userSelected: action.userSelected,
      saved: action.save,
    },
  }),
  [CHECKOUT_INITIALISE]: () => ({
    experienceTimes: {},
    experienceDates: {},
    experienceItemsValidation: {},
  }),
  [CHECKOUT_PROCESS_SUCCESS]: () => ({
    experienceTimes: {},
    experienceDates: {},
    experienceItemsValidation: {},
  }),
  [SET_CANCELLING_EXPERIENCES]: (state, action) => ({
    cancellingExperiences: action.experiences,
  }),
  [CLEAR_EXPERIENCE_TIMES]: (state, action) => ({
    experienceTimes: {
      ...state.experienceTimes,
      [action.experienceId]: {},
    },
  }),
  [CLEAR_EXPERIENCE]: (state, action) => {
    const experiences = { ...state.experiences }
    const experienceErrors = { ...state.experienceErrors }
    const fetchingExperiences = { ...state.fetchingExperiences }
    delete experiences[action.id]
    delete experienceErrors[action.id]
    delete fetchingExperiences[action.id]
    return { experiences, experienceErrors, fetchingExperiences }
  },
  [OFFER_SET_EXPERIENCES_SEARCH_RESULT_METADATA]: (state, action) => ({
    experienceOffersMetadata: {
      ...state.experienceOffersMetadata,
      [action.listKey]: action.metadata,
    },
  }),
})
